import React, { useState, useEffect } from 'react';
import { LinkIcon, DocumentTextIcon, EyeIcon, EyeSlashIcon, PencilIcon } from '@heroicons/react/24/outline';
import Modal from '../common/Modal';
import { Quotation } from '../../types/quotation';
import { useLanguage } from '../../contexts/LanguageContext';
import { useQuotations } from '../../hooks/useQuotations';
import toast from 'react-hot-toast';
import '../../styles/toggle-switch.css';

// Segédfüggvény a dátum formázásához, amely kezeli az undefined értékeket
const getFormattedDate = (date: any) => {
  if (!date) return '';

  try {
    // Ha a date objektumnak van toDate metódusa (Firestore Timestamp)
    if (date && typeof date === 'object' && 'toDate' in date && typeof date.toDate === 'function') {
      const jsDate = date.toDate();
      return `${jsDate.toLocaleDateString()} ${jsDate.toLocaleTimeString()}`;
    }

    // Ha a date egy JavaScript Date objektum
    if (date instanceof Date) {
      return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
    }

    // Ha string vagy egyéb formátum, próbáljuk meg Date objektummá konvertálni
    const jsDate = new Date(date as any);
    return `${jsDate.toLocaleDateString()} ${jsDate.toLocaleTimeString()}`;
  } catch (error) {
    console.error('Hiba a dátum formázása során:', error);
    return '';
  }
};

interface ShareQuotationModalProps {
  isOpen: boolean;
  onClose: () => void;
  quotation: Quotation | null;
}

const ShareQuotationModal: React.FC<ShareQuotationModalProps> = ({
  isOpen,
  onClose,
  quotation
}) => {
  const { t } = useLanguage();
  const { updateQuotation } = useQuotations();

  const [isPasswordProtected, setIsPasswordProtected] = useState(true);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [shareLinkCreated, setShareLinkCreated] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [validUntil, setValidUntil] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);

  // Modal megnyitásakor ellenőrizzük a meglévő megosztási beállításokat
  useEffect(() => {
    if (isOpen && quotation && quotation.shareSettings) {
      setIsPasswordProtected(quotation.shareSettings.isPasswordProtected);
      setPassword(quotation.shareSettings.password || '');
      setShareLinkCreated(true);

      const url = `${window.location.origin}/quotations/public/${quotation.id}${quotation.shareSettings?.shareId ? `?share=${quotation.shareSettings.shareId}` : ''
        }`;
      setShareLink(url);

      if (quotation.shareSettings.validUntil) {
        try {
          const date = quotation.shareSettings.validUntil instanceof Date
            ? quotation.shareSettings.validUntil
            : new Date(quotation.shareSettings.validUntil);
          setValidUntil(date.toISOString().slice(0, 16));
        } catch (error) {
          console.error("Hiba a dátum konvertálása során:", error);
        }
      } else {
        setValidUntil('');
      }
    } else {
      // Ha nincs még megosztási beállítás, vagy új megosztást kezdünk
      setIsPasswordProtected(true);
      setPassword('');
      setShareLinkCreated(false);
      setShareLink('');
      setValidUntil('');
      setIsEditing(false);
    }
  }, [isOpen, quotation]);

  // Megosztási link generálása vagy frissítése
  const generateOrUpdateShareLink = async () => {
    if (!quotation) return;

    try {
      setIsLoading(true);

      // Átalakítjuk a validUntil stringet Date objektummá, ha van megadva
      let validUntilDate: Date | null = null;
      if (validUntil) {
        validUntilDate = new Date(validUntil);
      }

      // A shareSettings objektum felépítése
      const shareSettings = {
        shareId: quotation.shareSettings?.shareId || crypto.randomUUID(), // Új azonosító vagy a meglévő
        isPasswordProtected,
        password: isPasswordProtected ? password : null,
        validUntil: validUntilDate,
        createdAt: quotation.shareSettings?.createdAt || new Date(),
        viewCount: quotation.shareSettings?.viewCount || 0,
        lastViewed: quotation.shareSettings?.lastViewed || null
      };

      // Ha nincs még publicToken, akkor generálunk egyet
      const publicToken = quotation.publicToken || crypto.randomUUID();

      // Frissítjük az árajánlatot az új megosztási beállításokkal
      await updateQuotation(quotation.id, {
        ...quotation,
        publicToken,
        shareSettings
      });

      // Beállítjuk a megosztási linket
      const url = `${window.location.origin}/quotations/public/${quotation.id}${shareSettings.shareId ? `?share=${shareSettings.shareId}` : ''
        }`;
      setShareLink(url);
      setShareLinkCreated(true);

      // Ha szerkesztési módban voltunk, akkor lépjünk ki belőle
      setIsEditing(false);

    } catch (error) {
      console.error('Hiba a megosztási link létrehozásakor:', error);
      toast.error(t('common.messages.shareError') || 'Hiba történt a megosztás közben!');
    } finally {
      setIsLoading(false);
    }
  };

  // Megosztási link másolása a vágólapra
  const copyLinkToClipboard = () => {
    navigator.clipboard.writeText(shareLink)
      .then(() => {
        toast.success(t("quotations.shared") || "A link másolva a vágólapra!");
        onClose();
      })
      .catch(err => {
        console.error("Nem sikerült másolni a vágólapra:", err);
        toast.error(t("common.messages.copyError") || "Nem sikerült másolni a vágólapra!");
      });
  };

  // Szerkesztési mód bekapcsolása
  const handleEdit = () => {
    setIsEditing(true);
  };

  // Szerkesztés visszavonása
  const handleCancelEdit = () => {
    // Visszaállítjuk az eredeti értékeket
    if (quotation && quotation.shareSettings) {
      setIsPasswordProtected(quotation.shareSettings.isPasswordProtected);
      setPassword(quotation.shareSettings.password || '');

      if (quotation.shareSettings.validUntil) {
        try {
          const date = quotation.shareSettings.validUntil instanceof Date
            ? quotation.shareSettings.validUntil
            : new Date(quotation.shareSettings.validUntil);
          setValidUntil(date.toISOString().slice(0, 16));
        } catch (error) {
          console.error("Hiba a dátum konvertálása során:", error);
        }
      } else {
        setValidUntil('');
      }
    }

    setIsEditing(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("quotations.shareModal.title") || "Árajánlat megosztása"}
      size="lg"
    >
      <div className="p-6 space-y-5">
        {!shareLinkCreated || isEditing ? (
          <>
            <div className="bg-white rounded-lg p-4 mb-5 border border-gray-200 shadow-sm">
              <div className="flex items-center">
                <DocumentTextIcon className="h-7 w-7 text-primary-600 mr-3" />
                <div>
                  <h3 className="text-gray-800 font-medium">{quotation?.number}</h3>
                  <p className="text-gray-500 text-sm">{t("quotations.status." + quotation?.status)}</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="text-gray-700">
                  {t("quotations.shareModal.passwordProtection") || "Jelszavas védelem"}
                </label>
                <div>
                  {/* CSS osztályokkal megvalósított toggle switch */}
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={isPasswordProtected}
                      onChange={() => setIsPasswordProtected(!isPasswordProtected)}
                    />
                    <span className="slider"></span>
                  </label>
                </div>
              </div>

              {isPasswordProtected && (
                <div className="space-y-2">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-600">
                    {t("quotations.shareModal.password") || "Jelszó"}
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="mt-1 block w-full p-2.5 bg-white border border-gray-300 rounded-md text-gray-700 focus:ring-primary-500 focus:border-primary-500"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 flex items-center pr-3"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeSlashIcon className="h-5 w-5 text-gray-500" />
                      ) : (
                        <EyeIcon className="h-5 w-5 text-gray-500" />
                      )}
                    </button>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <label htmlFor="valid-until" className="block text-sm font-medium text-gray-600">
                  {t("quotations.shareModal.validUntil") || "Érvényesség vége (opcionális)"}
                </label>
                <input
                  type="datetime-local"
                  id="valid-until"
                  value={validUntil}
                  onChange={(e) => setValidUntil(e.target.value)}
                  className="mt-1 block w-full p-2.5 bg-white border border-gray-300 rounded-md text-gray-700 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div className="pt-4 flex justify-end space-x-3">
                {isEditing ? (
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                  >
                    {t("common.cancel") || "Mégse"}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                  >
                    {t("common.cancel") || "Mégse"}
                  </button>
                )}
                <button
                  type="button"
                  onClick={generateOrUpdateShareLink}
                  disabled={isPasswordProtected && !password}
                  className={`px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors flex items-center ${(isPasswordProtected && !password) ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                >
                  {isLoading ? (
                    <><span className="animate-spin mr-2">⟳</span> {t("common.loading") || "Betöltés..."}</>
                  ) : (
                    <>{isEditing
                      ? (t("quotations.shareModal.updateLink") || "Link frissítése")
                      : (t("quotations.shareModal.createLink") || "Link létrehozása")}</>
                  )}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="space-y-5">
            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
              <p className="text-gray-600 mb-2">{t("quotations.shareModal.linkCreated") || "Megosztási link létrehozva:"}</p>
              <div className="flex items-center bg-gray-50 rounded-md p-2.5 border border-gray-200">
                <input
                  type="text"
                  readOnly
                  value={shareLink}
                  className="flex-grow bg-transparent border-0 text-gray-700 focus:ring-0 text-sm"
                />
                <button
                  type="button"
                  onClick={copyLinkToClipboard}
                  className="ml-2 text-primary-600 hover:text-primary-700"
                >
                  <LinkIcon className="h-5 w-5" />
                </button>
              </div>

              {/* Megtekintési adatok megjelenítése, ha elérhetők */}
              {quotation?.shareSettings?.viewCount !== undefined && (
                <div className="mt-3 flex items-center text-gray-500 text-sm">
                  <EyeIcon className="h-4 w-4 mr-2" />
                  <span>{t("quotations.viewCount", { count: quotation.shareSettings.viewCount })}</span>
                  {quotation.shareSettings.lastViewed && (
                    <span className="ml-3 text-xs">
                      {t("quotations.lastViewed")}: {getFormattedDate(quotation.shareSettings.lastViewed)}
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={handleEdit}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors flex items-center"
              >
                <PencilIcon className="h-4 w-4 mr-2" />
                {t("common.edit") || "Szerkesztés"}
              </button>

              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
              >
                {t("common.close") || "Bezárás"}
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ShareQuotationModal; 